import { useState, type ReactNode } from 'react';

interface CollapsibleSectionProps {
  /** Usually a plain string; REV6's `/pitched` boss groups pass a `<span>` (icon + name) instead. */
  title: ReactNode;
  subtitle?: string;
  defaultOpen?: boolean;
  /**
   * Optional header-row content rendered as a sibling of the toggle button (not nested inside
   * it — a `<button>` can't contain another interactive control) so it can carry its own click
   * handler without also collapsing the section. REV2 uses this for the boss tiers' "Complete
   * all" action; existing callers that omit it get byte-identical markup to before.
   */
  headerAction?: ReactNode;
  children: ReactNode;
}

/** A mobile-first collapsible card, used by every `/characters/:id` section (WP7 spec). */
export function CollapsibleSection({
  title,
  subtitle,
  defaultOpen = true,
  headerAction,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-border bg-bg-elevated">
      <div className="flex items-center gap-3 px-5 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex min-h-tap flex-1 items-center justify-between gap-3 text-left"
        >
          <span>
            <span className="block font-display text-base font-semibold text-text">{title}</span>
            {subtitle && <span className="block text-xs text-text-faint">{subtitle}</span>}
          </span>
          <span
            aria-hidden="true"
            className={['text-text-faint transition-transform duration-150', open ? 'rotate-180' : ''].join(' ')}
          >
            ▾
          </span>
        </button>
        {headerAction && <div className="shrink-0">{headerAction}</div>}
      </div>
      {open && <div className="space-y-4 border-t border-border p-5">{children}</div>}
    </section>
  );
}
